import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import AssistantChatWidget from "../assistant/AssistantChatWidget";

// Pages where the floating widget is hidden by default
const WIDGET_HIDDEN_PAGES = ["/chat", "/drawing-evaluation"];

/**
 * AppShell wraps the entire authenticated app.
 * Renders the persistent left sidebar and the main content area.
 * Manages floating chat widget visibility with manual override.
 */
export default function AppShell({ children, onLogout }) {
  const location = useLocation();

  // null = no manual override (use page defaults)
  // true/false = user manually toggled visibility, persists until refresh
  const [widgetOverride, setWidgetOverride] = useState(null);

  const isHiddenPage = WIDGET_HIDDEN_PAGES.some((p) => location.pathname.startsWith(p));

  // Effective visibility: manual override wins, otherwise use page-based defaults
  const widgetVisible = widgetOverride !== null ? widgetOverride : !isHiddenPage;

  const handleToggleWidget = useCallback(() => {
    setWidgetOverride((prev) => {
      if (prev === null) {
        // First toggle: flip from current default
        return isHiddenPage ? true : false;
      }
      return !prev;
    });
  }, [isHiddenPage]);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50" data-testid="app-shell">
      <Sidebar
        onLogout={onLogout}
        widgetVisible={widgetVisible}
        onToggleWidget={handleToggleWidget}
      />
      <main className="min-w-0 flex-1 overflow-hidden flex flex-col max-md:pt-14" data-testid="main-content">
        {children}
      </main>
      {widgetVisible && <AssistantChatWidget />}
    </div>
  );
}
